import { FC, useContext, useEffect, useState } from 'react';
import styles from './index.less';
import { useParams, useRequest } from 'umi';
import { Button, Form, message, Modal, Result, Spin } from 'antd';
import NodeInfoBasicForm from '@/pages/backend/cardCampSettings/nodeSettings/nodeInfoBasicForm';
import NodeContentList from '@/pages/backend/cardCampSettings/nodeSettings/nodeContentList';
import { ESwitchEnum } from '@/pages/backend/cardCampSettings/nodeSettings/switchEnum';
import { createNode, getNodeInfoById, updateNodeInfo } from '@/services/ConsoleRequest';
import { TConsoleSetingsNode } from '@/types/console/node';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty, isEqual, isEqualWith, isUndefined } from 'lodash';
import { ENodeType } from '@/pages/backend/cardCampSettings/nodeSettings/enum';
import { CardCampSettingContext } from '@/pages/backend/cardCampSettings/CardCampSettingContext';
import { EVideoUploadStatus } from '@/pages/backend/cardCampSettings/nodeSettings/videoCom/videoNode';

type TNodeInfo = {
  activeId?: string;
  activeIndex: number;
  onActiveIdChange: (id: string, name: string) => void;
};

const initFormValue = {
  remindDays: 0,
  remindTime: dayjs('18:00', 'HH:mm'),
  manualReview: ESwitchEnum.close,
  remark: ESwitchEnum.open,
  remind: ESwitchEnum.close,
  learn: ESwitchEnum.open,
};

const hasPendingVideoUpload = (contentInfoMapping: Record<string, any> | undefined) => {
  if (!contentInfoMapping) {
    return false;
  }
  return Object.values(contentInfoMapping).some(
    (item) =>
      Array.isArray(item?.video) &&
      item.video.some((video: any) => video?.uploadStatus === EVideoUploadStatus.loading),
  );
};

const NodeInfo: FC<TNodeInfo> = (props) => {
  const { onFormChange } = useContext(CardCampSettingContext);
  const { onActiveIdChange, activeId, activeIndex } = props;
  const { id: campId } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [nodeInfo, setNodeInfo] = useState<TConsoleSetingsNode | undefined>();
  const { run } = useRequest(
    (id: string) => {
      return getNodeInfoById(id as string);
    },
    {
      manual: true,
      onSuccess: (res) => {
        if (res.remind) {
          res.remindTime = dayjs(res.remindTime, 'HH:mm');
          res.remindDays = res.remindDays || initFormValue.remindDays;
        } else {
          // res.remindTime = initFormValue.remindTime;
        }
        if (Array.isArray(res.contents)) {
          let obj: Record<string, any> = {};
          for (let content of res.contents) {
            let info = content?.info?.contentInfo;
            if (content.type === ENodeType.certification) {
              info = { professionalCertification: info };
            } else if (content.type === ENodeType.richtext) {
              info = { richText: info };
            } else if (content.type === ENodeType.video) {
              info = { video: info };
            } else if (content.type === ENodeType.exercise) {
              info = { exercise: info };
            }
            console.log('info是', info);
            obj[content.id] = info;
          }
          res.contentInfoMapping = obj;
        }
        if (nodeInfo) {
          for (const field in nodeInfo) {
            const key = field as keyof TConsoleSetingsNode;
            if (isUndefined(res[key])) {
              // @ts-ignore
              res[key] = undefined;
            }
          }
          form.setFieldsValue(res);
        }
        console.log('最后的res是', res);
        console.log(nodeInfo);
        setNodeInfo({ ...res });
      },
    },
  );
  useEffect(() => {
    if (activeId) {
      if (!activeId.startsWith('newId')) {
        run(activeId);
      } else {
        form.resetFields();
        setNodeInfo(undefined);
      }
    }
  }, [activeId]);
  const formComponent = (
    <>
      <div className={styles.section}>
        <div className={styles.section_title}>基本设置</div>
        <NodeInfoBasicForm id={activeId} />
      </div>

      <div className={styles.section}>
        <div className={styles.section_title}>节点内容</div>
        <NodeContentList data={nodeInfo?.contents} />
      </div>
    </>
  );
  const isMyEqual = (values: any) => {
    const cloneDeepValues = cloneDeep(values);
    const cloneDeepNodeInfo = cloneDeep(nodeInfo) as any;
    delete cloneDeepNodeInfo?.id;
    delete cloneDeepNodeInfo?.index;
    delete cloneDeepNodeInfo?.cardCampId;
    if (!('description' in cloneDeepNodeInfo)) {
      cloneDeepNodeInfo.description = undefined;
    }
    delete cloneDeepNodeInfo.contentInfoMapping;
    delete cloneDeepValues.contentInfoMapping;
    return isEqualWith(cloneDeepValues, cloneDeepNodeInfo, isEqual);
  };
  const renderForm = () => {
    if (activeId?.startsWith('newId')) {
      return (
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 16 }}
          initialValues={initFormValue}
          onValuesChange={(changedValues, values) => {
            console.log('新建了', changedValues, values);
            onFormChange?.(true);
          }}
        >
          {formComponent}
        </Form>
      );
    } else {
      if (!nodeInfo) {
        return <Spin />;
      }
      return (
        <Form
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 16 }}
          initialValues={{
            ...nodeInfo,
          }}
          onValuesChange={(changedValues, values) => {
            if (nodeInfo) {
              if (isMyEqual(values)) {
                onFormChange?.(false);
              } else {
                onFormChange?.(true);
              }
            } else {
              onFormChange?.(true);
            }
          }}
        >
          {formComponent}
        </Form>
      );
    }
  };

  return (
    <>
      {activeId ? (
        <>
          <div className={styles.node_settings_right_inner}>
            <div className={styles.node_settings_right_inner_title}>节点信息</div>
            {renderForm()}
          </div>
          <div className={styles.node_settings_right_footer}>
            <Button
              type="primary"
              onClick={async () => {
                const scrollToFirstError = () => {
                  document
                    .querySelector('.ant-form-item-has-error')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                };
                try {
                  const data = await form.validateFields();
                  if (hasPendingVideoUpload(data.contentInfoMapping)) {
                    message.warning('视频上传未完成，请等待上传完成后再保存');
                    return;
                  }
                  if (isEmpty(data.contents)) {
                    message.warning('请设置节点内容');
                  } else {
                    data.cardCampId = campId;
                    const contents = data.contents;
                    data.index = activeIndex;
                    if (Array.isArray(contents)) {
                      for (const item of contents) {
                        const id = item.id;
                        if (data.contentInfoMapping[id]) {
                          item.info = data.contentInfoMapping[id];
                        }
                      }
                    }
                    if (data.remindTime) {
                      data.remindTime = dayjs(data.remindTime).format('HH:mm');
                    }
                    Modal.confirm({
                      title: `确认保存吗？`,
                      okText: '确认',
                      onOk: async () => {
                        console.log('最后提交的数据是', data);

                        const res = activeId?.startsWith('newId')
                          ? await createNode(data)
                          : await updateNodeInfo(activeId, data);
                        if (res.code === 200) {
                          onFormChange?.(false);
                          message.success('保存成功', 1, () => {
                            console.log('res是', res);
                            if (activeId.startsWith('newId')) {
                              onActiveIdChange(res.data.id, res.data.name);
                            } else {
                              if (nodeInfo?.name !== res.data.name) {
                                onActiveIdChange(res.data.id, res.data.name);
                              }
                              run(activeId);
                            }
                            // location.reload();
                          });
                        } else {
                          message.warning('保存失败请稍后重试');
                        }
                      },
                    });
                  }
                } catch (e) {
                  scrollToFirstError();
                }
              }}
            >
              保存修改
            </Button>
          </div>
        </>
      ) : (
        <Result
          subTitle={<div>请先在左侧选择打卡节点</div>}
          icon={<img src={'https://hwobs.fanruan.com/ui/warning.png'} width={180} />}
        />
      )}
    </>
  );
};
export default NodeInfo;
